import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import DownloadIcon from '@mui/icons-material/Download';
import { getOperatorProjectMatrix, getOperatorProjectMatrixExportUrl } from '../api';
import { getToken } from '../auth';

interface MatrixCell {
  sent: number;
  approved: number;
}

interface ProjectInfo {
  id: number;
  name: string;
}

interface MatrixData {
  month: string;
  operators: string[];
  projects: ProjectInfo[];
  matrix: Record<string, Record<string, MatrixCell>>;
  totals: {
    byOperator: Record<string, MatrixCell>;
    byProject: Record<string, MatrixCell>;
    grand: MatrixCell;
  };
}

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export const OperatorProjectMatrixPage: React.FC = () => {
  const [month, setMonth] = useState(getCurrentMonth());
  const [data, setData] = useState<MatrixData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getOperatorProjectMatrix(month);
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    const url = getOperatorProjectMatrixExportUrl(month);
    const token = getToken();
    try {
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `operator-project-matrix-${month}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err: any) {
      setError(err.message || 'Ошибка экспорта');
    }
  };

  const cellValue = (cell: MatrixCell | undefined, type: 'sent' | 'approved') => {
    const val = cell?.[type] || 0;
    return val > 0 ? val : '';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">Матрица: Оператор / Проект</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            size="small"
            sx={{ width: 200 }}
          />
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={!data || data.operators.length === 0}
          >
            Скачать Excel
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && data && data.operators.length === 0 && (
        <Alert severity="info">Нет данных за выбранный период</Alert>
      )}

      {!loading && data && data.operators.length > 0 && (
        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 200px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  rowSpan={2}
                  sx={{
                    fontWeight: 'bold',
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                    backgroundColor: '#1976d2',
                    color: 'white',
                    minWidth: 180,
                  }}
                >
                  Оператор
                </TableCell>
                {data.projects.map((proj) => (
                  <TableCell
                    key={proj.id}
                    colSpan={2}
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#1976d2',
                      color: 'white',
                      borderLeft: '1px solid rgba(255,255,255,0.3)',
                      whiteSpace: 'nowrap',
                      fontSize: '0.75rem',
                    }}
                  >
                    {proj.name}
                  </TableCell>
                ))}
                <TableCell
                  colSpan={2}
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#0d47a1',
                    color: 'white',
                    borderLeft: '2px solid rgba(255,255,255,0.5)',
                  }}
                >
                  ИТОГО
                </TableCell>
              </TableRow>
              <TableRow>
                {[...data.projects, { id: -1, name: 'ИТОГО' }].map((proj) => (
                  <React.Fragment key={`sub-${proj.id}`}>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: proj.id === -1 ? '#0d47a1' : '#1565c0',
                        color: 'white',
                        fontSize: '0.7rem',
                        px: 0.5,
                        borderLeft: proj.id === -1 ? '2px solid rgba(255,255,255,0.5)' : '1px solid rgba(255,255,255,0.3)',
                        minWidth: 45,
                      }}
                    >
                      Пер.
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: proj.id === -1 ? '#0d47a1' : '#1565c0',
                        color: 'white',
                        fontSize: '0.7rem',
                        px: 0.5,
                        minWidth: 45,
                      }}
                    >
                      Утв.
                    </TableCell>
                  </React.Fragment>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.operators.map((op, idx) => (
                <TableRow key={op} sx={{ backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white' }}>
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      position: 'sticky',
                      left: 0,
                      backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white',
                      zIndex: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {op}
                  </TableCell>
                  {data.projects.map((proj) => {
                    const cell = data.matrix[op]?.[String(proj.id)];
                    return (
                      <React.Fragment key={`${op}-${proj.id}`}>
                        <TableCell
                          align="center"
                          sx={{
                            borderLeft: '1px solid #e0e0e0',
                            color: (cell?.sent || 0) > 0 ? 'inherit' : '#bbb',
                          }}
                        >
                          {cellValue(cell, 'sent')}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            color: (cell?.approved || 0) > 0 ? 'inherit' : '#bbb',
                          }}
                        >
                          {cellValue(cell, 'approved')}
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      borderLeft: '2px solid #1976d2',
                      backgroundColor: '#e3f2fd',
                    }}
                  >
                    {data.totals.byOperator[op]?.sent || 0}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#e3f2fd',
                    }}
                  >
                    {data.totals.byOperator[op]?.approved || 0}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: '#e3f2fd',
                    zIndex: 1,
                  }}
                >
                  ИТОГО
                </TableCell>
                {data.projects.map((proj) => {
                  const projTotal = data.totals.byProject[String(proj.id)];
                  return (
                    <React.Fragment key={`total-${proj.id}`}>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 'bold', borderLeft: '1px solid #e0e0e0' }}
                      >
                        {projTotal?.sent || 0}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        {projTotal?.approved || 0}
                      </TableCell>
                    </React.Fragment>
                  );
                })}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    borderLeft: '2px solid #1976d2',
                    backgroundColor: '#bbdefb',
                  }}
                >
                  {data.totals.grand.sent}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: 'bold', backgroundColor: '#bbdefb' }}
                >
                  {data.totals.grand.approved}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
